import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { AlertTriangle, CheckCircle, XCircle, Clock, Play, Pause } from 'lucide-react';
import { useValidationEngine } from '@/hooks/useValidationEngine';

export const ValidationEngine: React.FC = () => {
  const {
    validationRules,
    activeValidations,
    validationStats,
    isRunning,
    startValidation,
    pauseValidation,
    getValidationResults
  } = useValidationEngine();

  const [selectedRule, setSelectedRule] = useState<string | null>(null);

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'destructive';
      case 'high': return 'destructive';
      case 'medium': return 'secondary';
      case 'low': return 'outline';
      default: return 'outline';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'passed': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'failed': return <XCircle className="h-4 w-4 text-red-500" />;
      case 'warning': return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      case 'running': return <Clock className="h-4 w-4 text-blue-500" />;
      default: return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Validation Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              <div>
                <p className="text-sm font-medium">Passed</p>
                <p className="text-2xl font-bold">{validationStats.passed}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <XCircle className="h-5 w-5 text-red-500" />
              <div>
                <p className="text-sm font-medium">Failed</p>
                <p className="text-2xl font-bold">{validationStats.failed}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <AlertTriangle className="h-5 w-5 text-yellow-500" />
              <div>
                <p className="text-sm font-medium">Warnings</p>
                <p className="text-2xl font-bold">{validationStats.warnings}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <Clock className="h-5 w-5 text-blue-500" />
              <div>
                <p className="text-sm font-medium">Running</p>
                <p className="text-2xl font-bold">{validationStats.running}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Validation Controls */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Validation Engine</span>
            <div className="flex items-center space-x-2">
              <Button
                onClick={isRunning ? pauseValidation : startValidation}
                variant={isRunning ? "outline" : "default"}
                size="sm"
              >
                {isRunning ? (
                  <>
                    <Pause className="h-4 w-4 mr-2" />
                    Pause
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4 mr-2" />
                    Start Validation
                  </>
                )}
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isRunning && (
            <div className="mb-4">
              <div className="flex items-center justify-between text-sm text-muted-foreground mb-2">
                <span>Overall Progress</span>
                <span>{Math.round((validationStats.completed / validationStats.total) * 100)}%</span>
              </div>
              <Progress value={(validationStats.completed / validationStats.total) * 100} />
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Validation Rules */}
            <div>
              <h3 className="text-lg font-semibold mb-3">Validation Rules</h3>
              <div className="space-y-2">
                {validationRules.map((rule) => (
                  <div
                    key={rule.id}
                    className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                      selectedRule === rule.id ? 'border-primary bg-muted' : 'hover:bg-muted/50'
                    }`}
                    onClick={() => setSelectedRule(rule.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        {getStatusIcon(rule.status)}
                        <span className="font-medium">{rule.name}</span>
                      </div>
                      <Badge variant={getSeverityColor(rule.severity)}>
                        {rule.severity}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {rule.description}
                    </p>
                    {rule.status === 'pending' && rule.progress !== undefined && (
                      <Progress value={rule.progress} className="mt-2" />
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Active Validations */}
            <div>
              <h3 className="text-lg font-semibold mb-3">Active Validations</h3>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {activeValidations.map((validation) => (
                  <div key={validation.id} className="p-3 border rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">{validation.billId}</span>
                      <Badge variant="outline">{validation.stage}</Badge>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                      {getStatusIcon(validation.status)}
                      <span>{validation.message}</span>
                    </div>
                    {validation.status === 'running' && (
                      <Progress value={validation.progress} className="mt-2" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};